import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inicioDe } from "@/lib/inicio";
import { descargar, imprimirPdf } from "@/lib/download";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Download, Home, Package, Printer, RefreshCw, ScanLine, Search } from "lucide-react";
import { Escaner } from "@/components/empaque/Escaner";
import { Felicidades } from "@/components/Felicidades";
import { OrgPedido } from "@/components/organizador/OrgPedido";
import { OrgResumen } from "@/components/organizador/OrgResumen";
import { useToggle } from "@/components/organizador/OrgCeldas";

const TABS = [["organizador", "Organizador", "bg-[#F59E0B]"], ["empacados", "Empacados", "bg-[#16A34A]"], ["colocados", "Colocados", "bg-black"]];

const Encabezado = ({ abiertoCli, abiertoPago, togCli, togPago, tab }) => {
  const th = "px-2 py-2.5 text-[12px] font-bold text-white text-center whitespace-nowrap";
  return (
    <thead className="bg-black sticky top-0 z-10">
      <tr>
        <th className={th}>Pedido</th>
        <th className={th}><button onClick={togCli} className="inline-flex items-center gap-1" data-testid="org-toggle-cliente">Información del cliente {abiertoCli ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button></th>
        <th className={th}><button onClick={togPago} className="inline-flex items-center gap-1" data-testid="org-toggle-pago">Información de pago {abiertoPago ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button></th>
        {["Producto", "Características", "Comentarios", "Cantidad", "Paquete", "N° de guía", "Costo de guía", "Empacador", "Estado de Fabricación", "Estado de Empaque"].map((h) => <th key={h} className={th}>{h}</th>)}
        {tab !== "organizador" && <th className={th}>{tab === "empacados" ? "Empacado / Paquetes" : "Colocación"}</th>}
      </tr>
    </thead>
  );
};

export default function OrganizadorPaquetes() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("organizador");
  const [data, setData] = useState({ items: [], contadores: {}, resumen: [] });
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");
  const [qx, setQx] = useState("");
  const [archivados, setArchivados] = useState(false);
  const [empacadores, setEmpacadores] = useState([]);
  const [scan, setScan] = useState(false);
  const [ult, setUlt] = useState(null);
  const [fel, setFel] = useState(null);
  const [expo, setExpo] = useState(false);
  const [abiertoCli, togCli] = useToggle(true);
  const [abiertoPago, togPago] = useToggle(true);
  const [cargando, setCargando] = useState(false);

  const query = useMemo(() => new URLSearchParams({ desde, hasta, q: qx, archivados: archivados ? "true" : "false" }).toString(), [desde, hasta, qx, archivados]);
  const load = useCallback(async () => {
    setCargando(true);
    try { setData((await api.get(`/organizador/pedidos?tab=${tab}&${query}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setCargando(false); }
  }, [tab, query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/organizador/empacadores").then((r) => setEmpacadores(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setQx(q), 400); return () => clearTimeout(t); }, [q]);

  const onSaved = (row) => {
    if (row.tab !== tab) { toast.success(`${row.numero} pasó a ${row.tab === "empacados" ? "Empacados" : row.tab === "colocados" ? "Colocados" : "Organizador"}`); load(); return; }
    setData((d) => ({ ...d, items: d.items.map((x) => (x.id === row.id ? row : x)) }));
    api.get(`/organizador/pedidos?tab=${tab}&${query}`).then((r) => setData((d) => ({ ...d, resumen: r.data.resumen, contadores: r.data.contadores }))).catch(() => {});
  };
  const nuevoEmpacador = () => {
    const n = (window.prompt("Nombre del empacador") || "").trim();
    if (n) setEmpacadores((l) => [...new Set([...l, n])].sort());
    return n;
  };
  const leer = async (codigo) => {
    try {
      const { data: r } = await api.post("/organizador/escanear", { codigo });
      const f = r.faltan;
      setUlt({ ok: true, txt: `${r.pedido}: ${r.desc}${r.empacado ? " · ¡PEDIDO EMPACADO!" : ` · faltan ${f.productos} producto(s) y ${f.guias.length} guía(s)`}` });
      if (r.empacado) setFel({ id: Date.now(), completo: true, titulo: "¡FELICIDADES! 🎉", texto: `${r.pedido} EMPACADO` });
      if (navigator.vibrate) navigator.vibrate(120);
      load();
    } catch (e) {
      if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
      setUlt({ ok: false, txt: formatApiError(e) });
    }
  };
  const exportar = (fmt) => { setExpo(false); descargar(`/organizador/reporte.${fmt}?tab=${tab}&${query}`, `organizador_${tab}.${fmt}`).catch((e) => toast.error(String(e.message || e))); };

  return (
    <div className="min-h-screen bg-white text-black pb-64" data-testid="organizador-page">
      <header className="bg-black text-white px-3 sm:px-4 py-3 flex flex-wrap items-center gap-3">
        <button onClick={() => nav(inicioDe(user))} className="p-1" aria-label="Inicio" data-testid="org-home"><Home className="w-6 h-6" /></button>
        <div className="flex items-center gap-2 mr-auto">
          <Package className="w-10 h-10 text-[#F59E0B]" />
          <div><div className="text-xl sm:text-2xl font-black leading-tight">Organizador de Paquetes</div><div className="text-sm text-zinc-300">Empaque – Departamentos</div></div>
        </div>
        <div className="flex items-center gap-1 bg-white text-black rounded-md px-2 h-11" data-testid="org-fechas">
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="text-sm font-bold bg-transparent" data-testid="org-desde" />
          <span>–</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="text-sm font-bold bg-transparent" data-testid="org-hasta" />
        </div>
        <div className="flex items-center bg-white text-black rounded-md px-3 h-11 w-full sm:w-72">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, cliente o producto…" className="flex-1 bg-transparent text-sm outline-none" data-testid="org-buscar" />
          <Search className="w-5 h-5" />
        </div>
        <button onClick={() => { setUlt(null); setScan(true); }} className="h-11 px-4 rounded-md bg-[#16A34A] font-black flex items-center gap-2" data-testid="org-escanear"><ScanLine className="w-6 h-6" /> Escanear</button>
        <div className="relative">
          <button onClick={() => setExpo((x) => !x)} className="h-11 px-4 rounded-md bg-[#F59E0B] font-black flex items-center gap-2" data-testid="org-exportar"><Download className="w-5 h-5" /> Exportar <ChevronDown className="w-4 h-4" /></button>
          {expo && (
            <div className="absolute right-0 mt-1 w-36 rounded-md bg-white text-black shadow-xl border z-40">
              <button onClick={() => exportar("xlsx")} className="block w-full text-left px-3 py-2 hover:bg-zinc-100 font-bold" data-testid="org-exportar-xlsx">Excel</button>
              <button onClick={() => exportar("pdf")} className="block w-full text-left px-3 py-2 hover:bg-zinc-100 font-bold" data-testid="org-exportar-pdf">PDF</button>
            </div>
          )}
        </div>
        <button onClick={() => imprimirPdf(`/organizador/reporte.pdf?tab=${tab}&${query}`).catch((e) => toast.error(String(e.message || e)))} className="h-11 px-4 rounded-md bg-white text-black font-black flex items-center gap-2" data-testid="org-imprimir"><Printer className="w-5 h-5" /> Imprimir</button>
      </header>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b">
        {TABS.map(([k, l, c]) => (
          <button key={k} onClick={() => setTab(k)} className={`h-10 px-4 rounded-md font-black text-sm uppercase tracking-wide ${tab === k ? `${c} text-white` : "bg-zinc-100 text-zinc-700"}`} data-testid={`org-tab-${k}`}>
            {l} ({data.contadores?.[k] ?? 0})
          </button>
        ))}
        <label className="flex items-center gap-1 text-sm ml-2"><input type="checkbox" checked={archivados} onChange={(e) => setArchivados(e.target.checked)} data-testid="org-archivados" /> Ver archivados (períodos cerrados)</label>
        <button onClick={load} className="ml-auto p-2" aria-label="Actualizar" data-testid="org-recargar"><RefreshCw className={`w-5 h-5 ${cargando ? "animate-spin" : ""}`} /></button>
      </div>
      <div className="overflow-x-auto px-2 sm:px-3">
        <table className="w-full border-collapse min-w-[1400px]" data-testid="org-tabla">
          <Encabezado abiertoCli={abiertoCli} abiertoPago={abiertoPago} togCli={togCli} togPago={togPago} tab={tab} />
          <tbody>
            {data.items.map((r) => (
              <OrgPedido key={r.id} r={r} tab={tab} abiertoCli={abiertoCli} abiertoPago={abiertoPago} empacadores={empacadores} onSaved={onSaved} onNuevoEmpacador={nuevoEmpacador} />
            ))}
          </tbody>
        </table>
        {data.items.length === 0 && !cargando && <div className="text-center text-zinc-500 py-16" data-testid="org-vacio">Sin pedidos en {TABS.find((t) => t[0] === tab)[1]}.</div>}
      </div>
      <OrgResumen resumen={data.resumen || []} query={query} />
      {scan && (
        <Escaner titulo="ESCANEAR PRODUCTO O GUÍA" placeholder="QR del producto o número de guía" continuo onCode={leer} onClose={() => { setScan(false); load(); }}>
          {ult && <div className={`p-3 text-base font-bold ${ult.ok ? "bg-emerald-600" : "bg-red-700"}`} data-testid="org-scan-resultado">{ult.txt}</div>}
          <Felicidades msg={fel} />
        </Escaner>
      )}
    </div>
  );
}
